/* eslint-disable @typescript-eslint/naming-convention */
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { ComponentOptionLocationFieldType } from 'features/MultiCategory/utils/componentOptionLocation'
import { ComponentOptionScheduleFieldsType } from 'features/MultiCategory/utils/componentOptionSchedule'

export type HomeTabTypeResponseItemType = {
  id: number
  /** Snake-cased key, e.g. `demand_expansion`. */
  type: string
}

export type GetHomeTabTypesResponseType = {
  data: HomeTabTypeResponseItemType[]
}

/** Master list behind the Tabbing Type field — `homepage_tabbing_type_id` values. */
export const getHomeTabTypes = () =>
  axiosInstanceApiGateway.Get<GetHomeTabTypesResponseType, true>({
    url: '/browse/internal/v1/homepage-tabbings/types',
  })

export type HomeTabComponentTypeItemType = {
  id: number
  /** Stable code, e.g. `banner` for Banner Regular. */
  code: string
  name: string
  /** Type reserved for the "Semua" tab; superseded the `metadata` flag. */
  all_tab_only?: boolean
  /** JSON-encoded object, e.g. `{"all_tab_only": true}`; older responses sent a Struct. */
  metadata?: string | Record<string, unknown> | null
}

export type GetHomeTabComponentTypesResponseType = {
  data: HomeTabComponentTypeItemType[]
}

export const getHomeTabComponentTypes = () =>
  axiosInstanceApiGateway.Get<GetHomeTabComponentTypesResponseType, true>({
    url: '/browse/internal/v1/homepage-tabbings/component-types',
  })

export type BannerComponentOptionItemType = {
  id: number
  name: string
  targeting_type_name?: string
  /** Every hub the banner runs on; older rows carry one hub and repeat the `id`. */
  location?: ComponentOptionLocationFieldType
} & ComponentOptionScheduleFieldsType

export type GetBannerComponentOptionsResponseType = {
  data: BannerComponentOptionItemType[]
}

export type GetBannerComponentOptionsParamsType = {
  /** Filters by banner name; omit to get every banner. */
  name?: string
  id?: number
}

/** Banner Regular master list for the component picker. */
export const getBannerComponentOptions = (params: GetBannerComponentOptionsParamsType = {}) =>
  axiosInstanceApiGateway.Get<GetBannerComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/banner/component/options',
    params,
  })

export type DynamicChannelComponentOptionItemType = {
  id: number
  name: string
  dynamic_channel_type_id: number
  targeting_type_name?: string
  /** Every hub the channel runs on; older rows carry one hub and repeat the `id`. */
  location?: ComponentOptionLocationFieldType
} & ComponentOptionScheduleFieldsType

export type GetDynamicChannelComponentOptionsResponseType = {
  data: DynamicChannelComponentOptionItemType[]
}

export type GetDynamicChannelComponentOptionsParamsType = {
  /** Filters by channel name; omit to get every channel of the type. */
  name?: string
  id?: number
  dynamic_channel_type_id?: number
}

/** Dynamic channel master list for the component picker. */
export const getDynamicChannelComponentOptions = (
  params: GetDynamicChannelComponentOptionsParamsType = {},
) =>
  axiosInstanceApiGateway.Get<GetDynamicChannelComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/dynamic-channel/component/options',
    params,
  })

export type BannerThematicComponentOptionItemType = {
  id: number
  name: string
  targeting_type_name?: string
  /** Every hub the thematic runs on; older rows carry one hub and repeat the `id`. */
  location?: ComponentOptionLocationFieldType
} & ComponentOptionScheduleFieldsType

export type GetBannerThematicComponentOptionsResponseType = {
  data: BannerThematicComponentOptionItemType[]
}

export type GetBannerThematicComponentOptionsParamsType = {
  /** Filters by thematic name; omit to get every thematic. */
  name?: string
  id?: number
}

/** Banner Thematic master list for the component picker. */
export const getBannerThematicComponentOptions = (
  params: GetBannerThematicComponentOptionsParamsType = {},
) =>
  axiosInstanceApiGateway.Get<GetBannerThematicComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/thematic/component/options',
    params,
  })

export type CatalogComponentOptionItemType = {
  id: number
  name: string
  catalog_type_id: number
  targeting_type_name?: string
  /** Every hub the catalogue runs on; older rows carry one hub and repeat the `id`. */
  location?: ComponentOptionLocationFieldType
} & ComponentOptionScheduleFieldsType

export type GetCatalogComponentOptionsResponseType = {
  data: CatalogComponentOptionItemType[]
}

export type GetCatalogComponentOptionsParamsType = {
  /** Filters by catalogue name; omit to get every catalogue of the type. */
  name?: string
  id?: number
  catalog_type_id?: number
}

/** Catalogue master list for the component picker. */
export const getCatalogComponentOptions = (params: GetCatalogComponentOptionsParamsType = {}) =>
  axiosInstanceApiGateway.Get<GetCatalogComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/catalog/component/options',
    params,
  })

export type CategoryComponentOptionItemType = {
  id: number
  name: string
  targeting_type_name?: string
  /** Every hub the category runs on; older rows carry one hub and repeat the `id`. */
  location?: ComponentOptionLocationFieldType
}

export type GetCategoryComponentOptionsResponseType = {
  data: CategoryComponentOptionItemType[]
}

export type GetComponentOptionsParamsType = {
  /** Filters by name; omit to get every record. */
  name?: string
  id?: number
}

/** Category master list for the component picker. */
export const getCategoryComponentOptions = (params: GetComponentOptionsParamsType = {}) =>
  axiosInstanceApiGateway.Get<GetCategoryComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/category/component/options',
    params,
  })

export type DlpComponentOptionItemType = {
  id: number
  name: string
  targeting_type_name?: string
  location?: ComponentOptionLocationFieldType
}

export type GetDlpComponentOptionsResponseType = {
  data: DlpComponentOptionItemType[]
}

/** Dynamic Landing Page master list for the component picker. */
export const getDlpComponentOptions = (params: GetComponentOptionsParamsType = {}) =>
  axiosInstanceApiGateway.Get<GetDlpComponentOptionsResponseType, true>({
    url: '/browse/internal/v1/dynamic-landing/component/options',
    params,
  })
